import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react';
import { User } from '@upstart/harmony/lib/legos/userTypes.js';
import { Dealer } from '@upstart/harmony/lib/legos/dealerTypes.js';
import { Intercom, boot, shutdown, update } from '@intercom/messenger-js-sdk';
import { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types.js';

type IntercomDealer = Pick<Dealer, 'id' | 'name'>;

export type IntercomUser = Pick<User, 'firstName' | 'lastName' | 'phone'> & {
  email: string;
  userHash: string;
  dealer: IntercomDealer;
};

type SessionArg = {
  user?: IntercomUser;
  dealers?: IntercomDealer[];
};

type IntercomProps = Omit<IntercomSettings, 'app_id'>;

type SessionPropsGetter = (_: SessionArg) => IntercomProps;

type SessionPropsSetter = (_: SessionArg) => void;

export interface UseIntercomHookReturn {
  clearSession: VoidFunction;
  hideLauncher: VoidFunction;
  updateSession: SessionPropsSetter;
  showLauncher: VoidFunction;
}

const IntercomContext = React.createContext<UseIntercomHookReturn>({
  clearSession: () => {},
  hideLauncher: () => {},
  showLauncher: () => {},
  updateSession: () => {},
});

const mapDealerToCompany = ({
  id,
  name,
}: IntercomDealer): { company_id: string; name: string } => ({
  company_id: id,
  name: name ?? '',
});

const getSessionProps: SessionPropsGetter = ({ dealers, user }) => {
  const { firstName, lastName, email, phone, userHash, dealer } = user ?? {};
  if (!(email && userHash && dealer)) {
    return {};
  }
  const props: IntercomProps = {
    email,
    user_hash: userHash,
    company: mapDealerToCompany(dealer),
  };
  props.name = [firstName, lastName].filter(Boolean).join(' ').trim();
  props.phone = phone ?? undefined;

  if (props.company && dealers) {
    props.companies = [
      // Sometimes intercom just picks the first company on list as active
      // So always put the active company on the top of the list
      props.company,
      ...dealers.filter(({ id }) => id !== dealer.id).map(mapDealerToCompany),
    ];
  }
  return props;
};

export const IntercomProvider: React.FC<
  PropsWithChildren<{ appId: string; user?: IntercomUser }>
> = ({ appId, children, user }) => {
  Intercom({ app_id: appId });

  const updateSession: SessionPropsSetter = (arg) =>
    update(getSessionProps(arg));

  const hasBootedUser = useRef(false);

  const setUpUser = (arg: IntercomProps): void => {
    if (hasBootedUser.current) {
      update(arg);
    } else {
      boot({ app_id: appId, ...arg });
      hasBootedUser.current = true;
    }
  };

  useEffect(() => {
    const session = getSessionProps({ user });
    if (session.email) {
      setUpUser(session);
    } else {
      shutdown();
      boot({ app_id: appId });
      hasBootedUser.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return React.createElement(
    IntercomContext.Provider,
    {
      value: {
        updateSession,
        showLauncher: () => {
          update({ hide_default_launcher: false });
        },
        hideLauncher: () => {
          update({ hide_default_launcher: true });
        },
        clearSession: shutdown,
      },
    },
    children,
  );
};

export type IntercomContextProp = { appId: string; user?: IntercomUser };

export const useIntercom = (): UseIntercomHookReturn =>
  useContext(IntercomContext);
